import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import { usePrincipalKey } from "@auth/sessionStore";
import { useFrameRefresh } from "@notifications/useFrameRefresh";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tasksKeys } from "@tasks/tasksQueries";

export type CallSuggestion = components["schemas"]["SuggestionOut"];
export type CallSuggestionAutofile =
  components["schemas"]["SuggestionAutofileOut"];
type Edit = components["schemas"]["SuggestionEditIn"];
type Change =
  | { action: "approve" | "dismiss" }
  | { action: "edit"; body: Edit };
type Mutation = { item: CallSuggestion } & Change;

const FRAME_TYPES = ["phone_call_suggestions", "phone_call"];

export type CallSuggestionsQuery = ReturnType<typeof useCallSuggestions>;

export const useCallSuggestions = (callUuid: string) => {
  const principal = usePrincipalKey();
  const client = useQueryClient();
  const queryKey = ["call-suggestions", principal, callUuid] as const;
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      request(() =>
        apiClient.GET("/phone-calls/{call_uuid}/suggestions", {
          params: { path: { call_uuid: callUuid } },
          signal,
        }),
      ),
    enabled: principal !== "anon",
    staleTime: 5_000,
    retry: 1,
  });
  useFrameRefresh(FRAME_TYPES, callUuid, queryKey);
  const mutation = useMutation({
    mutationFn: ({ item, ...change }: Mutation) => {
      const params = {
        path: { call_uuid: callUuid, suggestion_uuid: item.uuid },
      };
      if (change.action === "edit")
        return request(() =>
          apiClient.PUT(
            "/phone-calls/{call_uuid}/suggestions/{suggestion_uuid}",
            { params, body: change.body },
          ),
        );
      const body = { revision: item.revision };
      return change.action === "approve"
        ? request(() =>
            apiClient.POST(
              "/phone-calls/{call_uuid}/suggestions/{suggestion_uuid}/approve",
              { params, body },
            ),
          )
        : request(() =>
            apiClient.POST(
              "/phone-calls/{call_uuid}/suggestions/{suggestion_uuid}/dismiss",
              { params, body },
            ),
          );
    },
    meta: { handled: true },
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey }),
        client.invalidateQueries({ queryKey: tasksKeys.all }),
      ]);
    },
  });
  const autofile = useMutation({
    mutationFn: (held: boolean) =>
      request(() =>
        apiClient.PUT("/phone-calls/{call_uuid}/suggestions/autofile", {
          params: { path: { call_uuid: callUuid } },
          body: { held },
        }),
      ),
    meta: { handled: true },
    onSuccess: (data) => {
      client.setQueryData(queryKey, data);
    },
    onSettled: async () => {
      await client.invalidateQueries({ queryKey });
    },
  });
  return { ...query, mutation, autofile };
};
